resourcePaths = '{"rootPath":"/member","assetsPath":"/member/assets"}';/*
    APIPresenter

    APIPresenter description.

    Parameters:

    Methods (see methods for additional usage):

        createModel(modelName, dataOrAction, callbackOrPromise)

        hasAction(action)

        performAction(action, data, callbackOrPromise)

        scanDOM(node)

        updateDOM(node)

        addComputedVariable(property,[variables],method)

        removeComputedVariable(property)

        extendViewMap(viewMap)

        addActionToNode(node, action)

        removeActionFromNode(node)

        addCallbackToAction(action, callback)

        removeCallbackFromAction(action, callbackId)

        updateModel(model, data)

        updateModels(models)

        resetModels(models)

        extendCustomValidations(action, validationObj)

        startPoll(models, interval)

        startPollAction(actions, interval, iterations, data)

        stopPoll(models)

        setModelProperty(property, value)

        removeModelProperty(property, value)

        getModelProperty(value)
*/
function APIPresenter () {
  this.models = {};

  /* Keeps additional custom validations specified on properties */
  this._customValidations = {};

  /*
    Format:
        this._actionCallbackMap = {
            "actionname": function(){}
        }
    */
  this._actionCallbackMap = {};

  /*
    Format:
        this._nodeActionMap = {
            "nodeId": action
        }
     */
  this._nodeActionMap = {};

  /*
    Format:
        this._contentMap = {
            "property.name": {
                "node_id": <node>,
                "node_id2": <node2>
            }
        }
    */
  this._contentMap = {};

  /*
    Format:
        this._bindMap = {
            "property.name": {
                "node_id": <node>,
                "node_id2": <node2>
            }
        }
    */
  this._bindMap = {};

  /* Defines view mappings for presenter/model properties.
        Also keeps computed variables.

        get() modifies variables before displaying on DOM
        set() modifies variables before sending to backend API  */
  this._viewMap = {};

  /* Keeps a list of nodes that depend on the presence
        or absence of a action link for their visibility.
    */
  this._actionViewBindMap = {};

  /* Keeps computed variables and associated methods.
       Format:
                this._computed = {
                    "computed_variable_name": {
                        variables: [model1.var1, model2.var2],
                        method: function(var1, var2)
                    }
                }
    */
  this._computed = {};

  this._computedVarDependencies = {};

  /* Keeps track of polling timers */
  this._pollTimers = {};

  // Used to create unique IDs
  this._idIncrementer = -1;
  this._bindIdIncrementer = -1;
  this._contentIdIncrementer = -1;
  this._actionViewBindIdIncrementer = -1;
}

/*
    Create a model in the presenter with a given name, with supplied data or
    supplied action (model.action). Supports nested models.

    Arguments:
        modelName [string] (required):
            The name to be used to reference the model.

        params [Object] (required):
            An object with the properties of data, uri, or action.
            The data, uri, or action properties are either the data that populates the
            model, or a uri or action in the form of model.action, in which the response
            is the data that populates the model.

        callbackOrPromise [function or Promise / Deferred] (optional):
            if a function, it is called after the model is created
            if a Promise or Deferred obj, it is resolved after the model is created

    Returns:
        [boolean]
            true if successful, false otherwise.
*/
APIPresenter.prototype.createModel = function (modelName, params, callbackOrPromise) {
  var doImmediateCallback = false;
  var actionResolved = false;
  var callbackArguments = null;

  if (typeof modelName !== 'string' || modelName.length === 0) {
    // Check for modelName
    doImmediateCallback = true;
  } else {
    var objRef = this.models;
    var modelNameIndex = modelName.lastIndexOf('.');
    var fullModelName = modelName;
    if (modelNameIndex > 0) {
      objRef = APIModel.prototype._getObjectProperty(
        this.models,
        modelName.substring(0, modelNameIndex)
      );
      modelName = modelName.substring(modelNameIndex + 1);
      if (!objRef) {
        doImmediateCallback = true;
      }
    }

    if (
      !doImmediateCallback &&
      params != null &&
      params.data != null &&
      typeof params.data === 'object'
    ) {
      // Create model - supplied data
      objRef[modelName] = new APIModel(modelName);
      objRef[modelName].updateModel(params.data, this._updateDependentDOM.bind(this, modelName));
      doImmediateCallback = true;
      actionResolved = true;
      callbackArguments = params.data;
    } else if (
      !doImmediateCallback &&
      params != null &&
      params.uri != null &&
      typeof params.uri === 'string'
    ) {
      // Create model - supplied uri
      objRef[modelName] = new APIModel(
        modelName,
        params.uri,
        this._onCreateModelActionCallback.bind(this, fullModelName, callbackOrPromise)
      );
    } else if (
      !doImmediateCallback &&
      params != null &&
      params.action != null &&
      typeof params.action === 'string' &&
      this.hasAction(params.action)
    ) {
      // Create model - supplied action
      objRef[modelName] = new APIModel(modelName);
      this.performAction(
        params.action,
        null,
        this._onCreateModelActionCallback.bind(this, fullModelName, callbackOrPromise)
      );
    } else if (!doImmediateCallback) {
      objRef[modelName] = new APIModel(modelName, null, function () {}, true);
      doImmediateCallback = true;
      actionResolved = true;
    }
  }

  if (doImmediateCallback && callbackOrPromise) {
    if (typeof callbackOrPromise === 'function') {
      callbackOrPromise(callbackArguments);
    } else if (actionResolved && typeof callbackOrPromise.resolve === 'function') {
      callbackOrPromise.resolve(callbackArguments);
    } else if (!actionResolved && typeof callbackOrPromise.reject === 'function') {
      callbackOrPromise.reject(callbackArguments);
    }
    return actionResolved;
  }
};

/*
    Checks whether an action in a given model exists in the current state of the
    presenter. Supports nested models.

    Arguments:
        action [string] (required):
            The name of the action in the form of model.action.

    Returns:
        [boolean]
            true if action exists, false otherwise.
*/
APIPresenter.prototype.hasAction = function (action) {
  if (typeof action !== 'string' || action.indexOf('.') < 0) return false;

  var index = action.indexOf('.');
  var model = action.substring(0, index);
  var modelAction = action.substring(index + 1);

  if (this.models[model]) {
    return this.models[model].hasAction(modelAction);
  }
  return false;
};

/*
    Checks whether an action exists and performs the action. Supports nested models.

    Arguments:
        action [string] (required):
            The name of the action.
        data [object] (optional):
            The data to be included in the execution of the action.
        callbackOrPromise [function or Promise / Deferred] (optional):
            if a function, it is called after the action is performed
            if a Promise or Deferred obj, it is resolved after the action is performed
        requestOptions [object]/[Integer] (optional):
            APIRequest options e.g. {polling_interval: 1, polling_timeout: 30} in seconds.
            APIRequest polling interval in milliseconds. Defaults to APIRequest default (1000).
            link.options from BE will override FE provided requestOptions params

    Returns:
        [boolean]
            false if action does not exist.
 */
APIPresenter.prototype.performAction = function (action, data, callbackOrPromise, requestOptions) {
  if (this.hasAction(action)) {
    var index = action.indexOf('.');
    var model = action.substring(0, index);
    var modelAction = action.substring(index + 1);

    var request = this.models[model].performAction(
      modelAction,
      data,
      this._onAction.bind(this, action, callbackOrPromise),
      requestOptions
    );
    return request;
  } else {
    if (callbackOrPromise) {
      if (typeof callbackOrPromise === 'function') callbackOrPromise(null, null);
      else if (typeof callbackOrPromise.reject === 'function') callbackOrPromise.reject(null, null);
    }
    return false;
  }
};

/*
    Scans DOM for bindings and data-actions:
    1) Creates validation on form (if any) using the right schema.
    2) Calls updateDOM to bind values to view

    Arguments:
        node [HTMLElement] (optional):
            The node to run scanDOM on. If undefined, runs on entire document.
*/
APIPresenter.prototype.scanDOM = function (node) {
  if (typeof node === 'undefined') node = $(document);
  // Look for all data-actions
  var dataActions = $(node).find('[data-action]');
  for (var i = 0; i < dataActions.length; i++) {
    // Map action to node
    this.addActionToNode(dataActions[i], $(dataActions[i]).attr('data-action'));

    // Create validation if form element
    if (
      dataActions[i] instanceof HTMLFormElement &&
      $(dataActions[i]).mogo().declaredClass == null
    ) {
      this._createValidation(dataActions[i]);
    }
  }

  // Look for content bindings
  this._createContentMap(node);

  // Look for data-bindings
  // Create map
  // Create change listeners (turn off and on listeners)
  this._createBindingMap(node);

  // Look for nodes dependent on action links
  this._createActionViewBindMap(node);

  this.updateDOM(node);
};

/*
    Populates binded nodes in the DOM with computed or model values.
    Updates action link dependent nodes' visibility.
    Arguments:
        node [HTMLElement]
            The node to run the update on. If undefined, runs on entire document.

*/
APIPresenter.prototype.updateDOM = function (node) {
  if (
    !node ||
    (!node[0] && !(node instanceof HTMLElement)) ||
    (node[0] && !(node[0] instanceof HTMLElement))
  ) {
    node = null;
  }

  // data-content nodes
  for (var contentItem in this._contentMap) {
    var value = this.getModelProperty(contentItem);
    if (typeof value === 'undefined') value = '';

    if (node) {
      for (var contentNodeId in this._contentMap[contentItem]) {
        if ($(node).attr('id') == contentNodeId || $(node).find('#' + contentNodeId).length > 0) {
          this._updateDependentNodes(this._contentMap, contentItem, value);
          break;
        }
      }
    } else {
      this._updateDependentNodes(this._contentMap, contentItem, value);
    }
  }

  // data-bind nodes
  for (var bind in this._bindMap) {
    if (node) {
      for (var bindNodeId in this._bindMap[bind]) {
        if ($(node).attr('id') == bindNodeId || $(node).find('#' + bindNodeId).length > 0) {
          this._updateDataBindNodeByKey(bind);
          break;
        }
      }
    } else {
      this._updateDataBindNodeByKey(bind);
    }
  }

  // Parse data-view nodes
  for (var actionNodeId in this._actionViewBindMap) {
    if (
      !node ||
      $(node).attr('id') == actionNodeId ||
      $(node).find('#' + actionNodeId).length > 0
    ) {
      this._updateDataViewNodeById(actionNodeId);
    }
  }
};

/*
    Adds a variable that is generated through computation involving variable(s) from possibly
    multiple models.

    For example; fullName can be a computed variable generated from account.first_name
    and account.last_name (account being the name of the model).
    One could then add fullName to the presenter using:

            this.addComputedVariable("fullName",
                                    ["account.first_name", "account.last_name"],
                                    function(firstName, lastName){
                                        return firstName + " " + lastName;
                                    });

    Arguments:
        property [string]
            name of the new variable
        variables [array of strings]
            names of existing model properties used to compute the new property's value.
            If no property is needed, send null instead of empty array
        method [function]
            the function that performs the computation. Its parameters are mapped with the variables provided.

*/
APIPresenter.prototype.addComputedVariable = function (property, variables, method) {
  if (variables && variables.length == 0) {
    console.warn(
      'No dependent properties were provided to the computed variable ' +
        property +
        '. This may degrade render performance.'
    );
  }
  var computedVariable = {};
  computedVariable[property] = {};
  computedVariable[property].variables = variables || [];
  computedVariable[property].method = method;
  $.extend(this._computed, computedVariable);

  for (var i = 0; i < computedVariable[property].variables.length; i++) {
    var variable = variables[i];
    if (typeof this._computedVarDependencies[variable] !== 'undefined') {
      this._computedVarDependencies[variable].push(property);
    } else {
      this._computedVarDependencies[variable] = [];
      this._computedVarDependencies[variable].push(property);
    }
  }

  this._scanDOMForDataBind(property);
  this._updateDependentDOM(property);
};

/*
    Removes a computed variable from the presenter.
    Arguments:
        property [string]
            the name of the computed property to be removed
*/
APIPresenter.prototype.removeComputedVariable = function (property) {
  if (this._computed[property] == undefined) return;

  var variables = this._computed[property].variables;
  delete this._computed[property];
  delete this._bindMap[property];
  for (var i = 0; i < variables.length; i++) {
    var variable = variables[i];
    var dependents = this._computedVarDependencies[variable];
    for (var func = 0; func < dependents.length; func++) {
      if (this._computedVarDependencies[variable][func] === property) {
        this._computedVarDependencies[variable].splice(func, 1);
      }
    }
  }
};

/*
    Extends viewMap object with the object provided. viewMap modifies the value of a property
    before adding it to a DOM element using the get method provided. It uses the set method
    to modify the value before sending it as a part of the data to the backend API.
    The function expects viewObj to be in the following format:
    {
        "model.property":{
            get: function(value){return value;},
            set: function(value){return value;}
        },
        "model.property2":{
            get: function(value){return value;},
            set: function(value){return value;}
        }
    }

    Arguments:
        viewObj [object]
            Object with the format above, which is appended to this._viewMap.

*/
APIPresenter.prototype.extendViewMap = function (viewObj) {
  $.extend(this._viewMap, viewObj);
};

/*
    Removes a property from the viewMap object as passed in through extendViewMap method.

    Arguments:
        viewObjProperty [String]
            Property that was previously added through extendViewMap, eg. "model.property".

    Returns:
        [boolean]
            true if successful, false otherwise.
*/
APIPresenter.prototype.removeViewMapProperty = function (viewObjProperty) {
  if (this._viewMap[viewObjProperty]) {
    delete this._viewMap[viewObjProperty];
    return true;
  }
  return false;
};

/*
    Adds or replaces an action on a HTML node. Supports nested models.

    Arguments:
        node [HTMLElement] (required):
            The node where the action should be added or replaced.
        action [string] (required):
            The action to be performed on that node.

    Returns:
        [boolean]
            true if action was successfully added to the given node, false otherwise.
*/
APIPresenter.prototype.addActionToNode = function (node, action) {
  if (node instanceof HTMLElement && action.split('.').length > 1) {
    $(node).attr('data-action', action);

    if (node.getAttribute('id') == null) {
      this._idIncrementer++;
      $(node).attr('id', 'Presenter_Action_' + this._idIncrementer);
    }
    this._nodeActionMap[node.getAttribute('id')] = action;

    if (!(node instanceof HTMLFormElement)) {
      // Remove event listener on node if available
      $(node).off('click.action');

      // Add event listener on node
      $(node).on('click.action', this._performActionOnNode.bind(this, node));
    }
    return true;
  }
  return false;
};

/*
    Removes action on a HTML node. Supports nested models.

    Arguments:
        node [HTMLElement] (required):
            The node where the action resides in.

    Returns:
        [boolean]
            true if action was successfully removed, false otherwise.
*/
APIPresenter.prototype.removeActionFromNode = function (node) {
  if (node.getAttribute('id') != null && this._nodeActionMap[node.getAttribute('id')] != null) {
    $(node).removeAttr('data-action');
    if (!(node instanceof HTMLFormElement)) {
      // Remove listener from node
      $(node).off('click.action');
    } else if (node instanceof HTMLFormElement) {
      return false;
    }
    delete this._nodeActionMap[node.getAttribute('id')];
    return true;
  }
  return false;
};

/*
    Switches the action on an HTML node to a new action. Supports nested models.

    Arguments:
        node [HTMLElement] (required):
            The node where the action resides in.
        newAction [string] (required):
            The new action to be performed on that node.

    Returns:
        [boolean]
            true if action was successfully switched, false otherwise.
*/
APIPresenter.prototype.switchActionOnNode = function (node, newAction) {
  // Invalid arguments
  if (!(node instanceof HTMLElement) || !this.hasAction(newAction) || $(node).attr('id') == null) {
    return false;
  }

  // Existing action
  if (this._nodeActionMap[$(node).attr('id')] == newAction) return false;

  // Remove current action
  this.removeActionFromNode(node);
  this.addActionToNode(node, newAction);

  // If form element and SchemaValidation component
  if (node instanceof HTMLFormElement && $(node).mogo().declaredClass == 'SchemaValidation') {
    var sv = $(node).mogo();
    // Remove schema
    sv.removeSchema(sv._defaultSchemaId);
    // Disable buttons
    var button = $(node).find('button[type="submit"]');
    if (button.length > 0) {
      $(button).each(function () {
        if (!$(this).mogo().create) {
          $(this).mogo().setDisabledLoadState();
        }
      });
    }
    // Add new schema
    var index = newAction.indexOf('.');
    var model = newAction.substring(0, index);
    var modelAction = newAction.substring(index + 1);

    var dataCache = $(node).attr('data-cache');
    var dataCacheBool = dataCache === '' || dataCache === 'true' || dataCache === true;
    this.models[model].getSchema(
      modelAction,
      this._onLoadSchemaAddToValidation.bind(this, newAction, node),
      dataCacheBool
    );
  }
};

/*
    Checks whether an action exists and returns the associated uri. Supports nested models.

    Arguments:
        action [string] (required):
            The name of the action.

    Returns:
        [string]
            uri string if it exists; null otherwise.
 */
APIPresenter.prototype.getActionUri = function (action) {
  if (this.hasAction(action)) {
    var index = action.indexOf('.');
    var model = action.substring(0, index);
    var modelAction = action.substring(index + 1);
    return this.models[model].getActionUri(modelAction);
  } else {
    return null;
  }
};

APIPresenter.prototype._onLoadSchemaAddToValidation = function (action, node, schema) {
  if (node instanceof HTMLFormElement && $(node).mogo().declaredClass == 'SchemaValidation') {
    var sv = $(node).mogo();
    sv.addSchema(schema);

    // Reset
    sv._initializeResultObj();
    sv._resultObj.submitted = false;
    sv._updateFormErrors(Object.keys(sv._resultObj._resultObj));
    var newValidationObj = {};
    if (this._customValidations[action]) {
      newValidationObj = this._customValidations[action];
    }
    sv.validationObj = newValidationObj;
  }
  // Enable buttons
  var button = $(node).find('button[type="submit"]');
  if (button.length > 0) {
    $(button).each(function () {
      if (!$(this).mogo().create) {
        $(this).mogo().removeDisabledLoadState();
      }
    });
  }
};

/*
    Add a callback function for a given action. Supports nested models.

    Arguments:
        action (string) [required]:
            The action to add the callback function to.
        callback (function) [required]:
            The callback function for the action.

    Returns:
        [integer]
            an positive integer id referencing the callback if successful, -1 otherwise.
*/
APIPresenter.prototype.addCallbackToAction = function (action, callback) {
  if (action.indexOf('.') >= 0 && typeof callback === 'function') {
    if (this._actionCallbackMap[action] == null) this._actionCallbackMap[action] = new Object();

    var uid = this._generateId();
    while (this._actionCallbackMap[action][uid] != undefined) {
      uid = this._generateId();
    }
    this._actionCallbackMap[action][uid] = callback;
    return uid;
  }
  return -1;
};

/*
    Removes the callback function for a given action. Supports nested models.

    Arguments:
        action (string) [required]:
            The action to where the callback should be removed.
        callbackId (integer) [required]:
            The callback id (which was returned on addCallbackToAction) of the
            function to be removed.

    Returns:
        [boolean]
            true if successful, false otherwise.
*/
APIPresenter.prototype.removeCallbackFromAction = function (action, callbackId) {
  if (this._actionCallbackMap[action] != null) {
    if (callbackId == null) {
      if (Object.keys(this._actionCallbackMap[action]).length == 1) {
        for (var key in this._actionCallbackMap[action]) {
          delete this._actionCallbackMap[action][key];
        }
      }
      console.warn(
        'APIPresenter: ' +
          'Method removeCallbackFromAction(action) is deprecated. ' +
          'Use removeCallbackFromAction(action, callback) instead.'
      );
    } else {
      if (this._actionCallbackMap[action][callbackId] != undefined) {
        delete this._actionCallbackMap[action][callbackId];
      }
    }

    if (Object.keys(this._actionCallbackMap[action]).length == 0) {
      delete this._actionCallbackMap[action];
    }
    return true;
  }
  return false;
};

/*
    Update a model with supplied data; or if no data is supplied, from the server.
    Supports nested models.

    Arguments:
        model [string] (required):
            The name of the model to be updated.
        data [object] (optional):
            The data to update the model with. If not supplied, retrieve from the
            server.
        callbackOrPromise [function or Promise / Deferred] (optional):
            if a function, it is called after the model is updated
            if a Promise or Deferred obj, it is resolved after the model is updated
*/
APIPresenter.prototype.updateModel = function (model, data, callbackOrPromise) {
  try {
    var index = model.indexOf('.');
    if (index !== -1) {
      // Nested model
      var currentModel = model.substring(0, index);
      var nestedModel = model.substring(index + 1);
      if (data != null) {
        this.models[currentModel].updateModel(data, callbackOrPromise, nestedModel);
        this._updateDependentDOM(model);
      } else {
        this.models[currentModel].updateModel(
          data,
          this._onUpdateSelf.bind(this, model, callbackOrPromise),
          nestedModel
        );
      }
      return;
    }

    if (data != null) {
      this.models[model].updateModel(data, this._updateDependentDOM.bind(this, model));
      if (callbackOrPromise) {
        if (typeof callbackOrPromise === 'function') callbackOrPromise();
        else if (typeof callbackOrPromise.resolve === 'function') callbackOrPromise.resolve();
      }
    } else {
      // Self action is called
      this.models[model].updateModel(data, this._onUpdateSelf.bind(this, model, callbackOrPromise));
    }
  } catch (e) {
    throw e;
  }
};

/*
    Updates the data of the model(s) from the server.

    Arguments:
        models [string || Array]:
            The name of the model, or an array with the names of the models.
*/
APIPresenter.prototype.updateModels = function (models) {
  try {
    if (typeof models === 'string') {
      models = [models];
    }

    for (var i = 0; i < models.length; i++) {
      this.updateModel(models[i], null, this._onUpdateSelf.bind(this, models[i], null));
    }
  } catch (e) {
    throw e;
  }
};

/*
    Resets model(s) to original state (i.e; before changes from frontend). Supports
    nested models.

    Arguments:
        models [string || Array]
            The name of the model, or an array with the names of the models.

*/
APIPresenter.prototype.resetModels = function (models) {
  /*
    TODO: check validation errors on reset. Due to changes in UI on 9-29 and changes
    in UI_core mogoliquid-m4 on loan-selection on 9-29.
  */
  if (typeof models === 'string') {
    models = [models];
  }

  for (var i = 0; i < models.length; i++) {
    var model = models[i];
    var index = model.indexOf('.');
    if (index === -1) {
      this.models[model].resetModel();
    } else {
      // Nested model
      this.models[model.substring(0, index)].resetModel(model.substring(index + 1));
    }
    this._updateDependentDOM(model);
  }
};

/*
    Adds a new property to model with the value specified.

    Arguments
        modelProperty [string]
            property name in the format "modelName.propertyName"
        value
            can be of any type
*/
APIPresenter.prototype.setModelProperty = function (modelProperty, value) {
  try {
    var index = modelProperty.indexOf('.');
    var model = modelProperty.substring(0, index);
    var property = modelProperty.substring(index + 1);

    this.models[model].setModelProperty(property, value);
    this._updateDependentDOM(modelProperty);
  } catch (e) {
    throw e;
  }
};

/*
    Removes a property from model.

    Arguments
        modelProperty [string]
            property name in the format "modelName.propertyName"
*/
APIPresenter.prototype.removeModelProperty = function (modelProperty) {
  try {
    var index = modelProperty.indexOf('.');
    var model = modelProperty.substring(0, index);
    var property = modelProperty.substring(index + 1);

    if (this.getModelProperty(modelProperty) === undefined) return;
    this.models[model].removeModelProperty(property);
    this._updateDependentDOM(modelProperty);

    // Clean-up bind map
    for (var key in this._bindMap) {
      if (key.indexOf(modelProperty) === 0) {
        delete this._bindMap[key];
      }
    }
  } catch (e) {
    throw e;
  }
};

/*
    Returns the value for specified property.

    Arguments
        modelProperty [string]
            property name in the format "modelName.propertyName"
    Returns
        value
            can be of any type
*/
APIPresenter.prototype.getModelProperty = function (modelProperty) {
  var index = modelProperty.indexOf('.');
  var model = modelProperty.substring(0, index);
  var property = modelProperty.substring(index + 1);

  if (this.models[model]) {
    return this.models[model].getModelProperty(property);
  } else {
    return undefined;
  }
};

/*
    Extend a validation object for a given action with custom validation rules. Method
    must be called before scanDOM is called on the node in which the form resides in
    for forms using Validation. Forms using the new SchemaValidation component can
    use this method to update a validationObj dynamically (at any time).

    Arguments:
        action [string] (required):
            The action the custom validations should apply to.

        validationObj [object] (required):
            The validation object containing custom validation rules.

    Returns:
        [boolean]
            true if successful, false otherwise.
*/
APIPresenter.prototype.extendCustomValidations = function (action, validationObj) {
  if (typeof validationObj === 'object') {
    this._customValidations[action] = validationObj;
    for (var key in this._nodeActionMap) {
      if (this._nodeActionMap[key] === action && $('#' + key).length > 0) {
        var obj = $('#' + key).mogo();
        if (obj.declaredClass && obj.declaredClass === 'SchemaValidation') {
          obj.validationObj = validationObj;
        }
      }
    }
    return true;
  } else return false;
};

/*
    Removes custom validation rules for a given action that was extended through
    extendCustomValidations().

    Arguments:
        action [string] (required):
            The action for which the custom validations should be removed.

    Returns:
        [boolean]
              true if successful, false otherwise.
*/
APIPresenter.prototype.removeCustomValidations = function (action) {
  if (this._customValidations[action]) {
    delete this._customValidations[action];
    for (var key in this._nodeActionMap) {
      if (this._nodeActionMap[key] === action && $('#' + key).length > 0) {
        var obj = $('#' + key).mogo();
        if (obj.declaredClass && obj.declaredClass === 'SchemaValidation') {
          obj.validationObj = {};
        }
      }
    }
    return true;
  }
  return false;
};

/*
    Enables updates to model(s) (calls self action on model) at a given interval.

    Arguments:
        models [string | Array] (required):
            The name of the model, or an array with the names of the models, to enable
            polling.
        interval [Number] (optional):
            The interval frequency of updates, in milliseconds. Defaults to 10000.
        iterations [Number] (optional):
            The number of iterations to make before stopping. Defaults to 50.
*/
APIPresenter.prototype.startPoll = function (models, interval, iterations) {
  if (typeof models === 'string') {
    this.startPollAction(models + '.self', interval, iterations);
  } else if (models instanceof Array) {
    for (var i = 0; i < models.length; i++) {
      this.startPollAction(models[i] + '.self', interval, iterations);
    }
  }
};

/*
    Performs an action on model(s) at a given interval.

    Arguments:
        actions [string | Array] (required):
            The names of the actions (model name and model action) to enable polling on.
        interval [Number] (optional):
            The interval frequency of updates, in milliseconds. Defaults to 10000.
        iterations [Number] (optional):
            The number of iterations to make before stopping. Defaults to 50.
*/
APIPresenter.prototype.startPollAction = function (actions, interval, iterations, data) {
  if (isNaN(parseInt(interval))) interval = 10000;
  else interval = parseInt(interval);

  if (isNaN(parseInt(iterations))) iterations = 50;
  else iterations = parseInt(iterations);

  if (typeof actions === 'string') {
    this._createPollTimer(actions, interval, iterations, data);
  } else if (actions instanceof Array) {
    for (var i = 0; i < actions.length; i++) {
      this._createPollTimer(actions[i], interval, iterations, data);
    }
  }
};

/*
    Disables continuous updates on model(s).

    Arguments:
        models [string | Array] (required):
            The name of the model, or an array with the names of the models, to disable
            polling.
*/
APIPresenter.prototype.stopPoll = function (models) {
  if (typeof models === 'string') {
    this.stopPollAction(models + '.self');
  } else if (models instanceof Array) {
    for (var i = 0; i < models.length; i++) {
      this.stopPollAction(models[i] + '.self');
    }
  }
};

/*
    Disables interval action calls on model(s).

    Arguments:
        actions [string | Array] (required):
            The names of the actions (model name and model action) to disable polling on.
*/
APIPresenter.prototype.stopPollAction = function (actions) {
  if (typeof actions === 'string') {
    this._deletePollTimer(actions);
  } else if (actions instanceof Array) {
    for (var i = 0; i < actions.length; i++) {
      this._deletePollTimer(actions[i]);
    }
  }
};

/*
    Retrieve the poll status for currently polling actions.

    Arguments
        actions [String | Array] (required):
            The names of the actions to get polling status on.
*/
APIPresenter.prototype.getPollStatus = function (actions) {
  var result = {};
  if (typeof actions === 'string') {
    if (this._pollTimers[actions] != null) {
      result[actions] = this._pollTimers[actions];
    }
  } else if (actions instanceof Array) {
    for (var i = 0; i < actions.length; i++) {
      if (this._pollTimers[actions[i]] != null) {
        result[actions[i]] = this._pollTimers[actions[i]];
      }
    }
  }
  return result;
};

APIPresenter.prototype._createPollTimer = function (action, interval, iterations, data) {
  if (typeof action === 'string' && this.hasAction(action)) {
    if (this._pollTimers[action] != null) {
      this.stopPollAction(action);
    }
    this._pollTimers[action] = {
      interval: interval,
      iterations: iterations
    };
    this._initPollTimerIteration(action, data);
  }
};

APIPresenter.prototype._deletePollTimer = function (action) {
  if (typeof action === 'string' && this._pollTimers[action] != null) {
    if (this._pollTimers[action].request) {
      this._pollTimers[action].request.cancel();
    }
    if (this._pollTimers[action].timeout) {
      clearTimeout(this._pollTimers[action].timeout);
    }
    delete this._pollTimers[action];
  }
};

APIPresenter.prototype._initPollTimerIteration = function (action, data) {
  const payload = data === 'undefined' ? null : data;
  var performPollTimerAction = function () {
    return this.performAction(action, payload, this._performPollActionIteration.bind(this, action, payload));
  }.bind(this);

  var pollTimer = this._pollTimers[action];
  if (pollTimer != null && !isNaN(pollTimer.iterations) && !isNaN(pollTimer.interval)) {
    if (pollTimer.request) {
      clearTimeout(pollTimer.timeout);
      pollTimer.request.cancel();
      pollTimer.timeout = setTimeout(
        function () {
          this._pollTimers[action].request = performPollTimerAction();
        }.bind(this),
        pollTimer.interval
      );
    } else {
      this._pollTimers[action].request = performPollTimerAction();
    }
  }
};

APIPresenter.prototype._performPollActionIteration = function (action, data) {
  var pollTimer = this._pollTimers[action];
  if (pollTimer != null) {
    pollTimer.iterations--;
    if (pollTimer.iterations > 0) {
      this._initPollTimerIteration(action, data);
    }
  }
};

APIPresenter.prototype._onUpdateSelf = function (model, callbackOrPromise, data, response, request) {
  this._updateDependentDOM(model);
  var action = model + '.self';
  this._onAction(action, callbackOrPromise, data, response, request);
};

APIPresenter.prototype._onAction = function (action, callbackOrPromise, data, response, request) {
  if (callbackOrPromise) {
    if (typeof callbackOrPromise === 'function') {
      callbackOrPromise(data, response, request);
    } else if (typeof callbackOrPromise.resolve === 'function') {
      callbackOrPromise.resolve(data, response, request);
    }
  }
  if (this._actionCallbackMap[action] != null) {
    for (var key in this._actionCallbackMap[action]) {
      if (this._actionCallbackMap[action][key] != undefined) {
        this._actionCallbackMap[action][key](data, response, request);
      }
    }
  }
};

APIPresenter.prototype._performActionOnNode = function (node) {
  if (node instanceof HTMLFormElement) {
    var data = null;
    if ($(node).data('validate') && $(node).data('validate') == 'auto') {
      data = $(node).mogo()._getFormData();
    } else data = $(node).serializeFormToObj();
    var actionPath = this._nodeActionMap[node.getAttribute('id')];
    var index = actionPath.indexOf('.');
    var model = actionPath.substring(0, index);
    var modelAction = actionPath.substring(index + 1);

    this.models[model].getSchema(
      modelAction,
      this._onLoadSchemaPerformActionOnNode.bind(this, node, data)
    );
  } else {
    this.performAction(this._nodeActionMap[node.getAttribute('id')], null);
  }
};

APIPresenter.prototype._onLoadSchemaPerformActionOnNode = function (node, data, schema) {
  /*
    extendViewMap configuration (for get/set)
    - model.property: reference when using data-bind
    - pathToAction.property: when not using data-bind or model doesn't exist
  */
  var nodeActionArr = this._nodeActionMap[node.getAttribute('id')].split('.');
  nodeActionArr.pop();
  var model = nodeActionArr.join('.');
  for (var attr in data) {
    var property = $(node).find('[name="' + attr + '"]').data('bind');
    if (this._viewMap[property] && this._viewMap[property].set) {
      data[attr] = this._viewMap[property].set(data[attr]);
    } else if (this._viewMap[model + '.' + attr] && this._viewMap[model + '.' + attr].set) {
      data[attr] = this._viewMap[model + '.' + attr].set(data[attr]);
    }
    if ($(node).data('validate') == null || $(node).data('validate') != 'auto') {
      if (typeof schema.properties[attr] === 'undefined') delete data[attr];
    }
  }
  // Get polling interval
  var pollingInterval = $(node).data('interval');
  if ($(node).data('interval') != null && !isNaN($(node).data('interval'))) {
    pollingInterval = parseInt(pollingInterval);
  } else {
    pollingInterval = null;
  }
  /* If a submit button exists for the action */
  var button = $(node).find('button[type="submit"]');
  /* And if it is a mogo component button with states */
  if (button.length > 0) {
    $(button).each(function () {
      if (!$(this).mogo().create) {
        $(this).mogo().setDisabledLoadState();
      }
    });
  }
  this.performAction(
    this._nodeActionMap[node.getAttribute('id')],
    data,
    this._processResponse.bind(this, node),
    pollingInterval
  );
};

APIPresenter.prototype._onCreateModelActionCallback = function (
  modelName,
  callbackOrPromise,
  data,
  response
) {
  if (response == 200) {
    var modelNameIndex = modelName.lastIndexOf('.');
    if (modelNameIndex > 0) {
      var objRef = this.models;
      objRef = APIModel.prototype._getObjectProperty(
        this.models,
        modelName.substring(0, modelNameIndex)
      );
      var fullModelName = modelName;
      modelName = modelName.substring(modelNameIndex + 1);
      if (objRef[modelName] === undefined) {
        objRef[modelName] = new APIModel(modelName);
      }
      objRef[modelName].updateModel(data, this._updateDependentDOM.bind(this, fullModelName));
    } else {
      if (typeof this.models[modelName] === 'undefined') {
        this.models[modelName] = new APIModel(modelName);
      }
      this.models[modelName].updateModel(data, this._updateDependentDOM.bind(this, modelName));
    }
    if (callbackOrPromise) {
      if (typeof callbackOrPromise === 'function') callbackOrPromise(data);
      else if (typeof callbackOrPromise.resolve === 'function') callbackOrPromise.resolve(data);
    }
  } else {
    if (callbackOrPromise) {
      if (typeof callbackOrPromise === 'function') callbackOrPromise(null, response);
      else if (typeof callbackOrPromise.reject === 'function') {
        callbackOrPromise.reject(null, response);
      }
    }
  }
};

APIPresenter.prototype._createValidation = function (node) {
  // Get data action
  var dataAction = this._nodeActionMap[node.getAttribute('id')];

  // Load schema
  if (this.hasAction(dataAction)) {
    var index = dataAction.indexOf('.');
    var model = dataAction.substring(0, index);
    var modelAction = dataAction.substring(index + 1);

    this.models[model].getSchema(modelAction, this._onLoadSchemaCreateValidation.bind(this, node));
  }
};

APIPresenter.prototype._onLoadSchemaCreateValidation = function (node, schema) {
  if ($(node).mogo().declaredClass != null) return false;
  if (
    $(node).data('validate') &&
    ($(node).data('validate') == 'auto' || $(node).data('validate') == 'only')
  ) {
    $(node).mogo().create('SchemaValidation', {
      schema: schema,
      validationObj: this._customValidations[$(node).data('action')] || {},
      onSubmit: this._onFormValidate.bind(this, node)
    });
  } else {
    console.warn('Validation is deprecated. Use schema validation instead.');
    var sa = new SchemaAdapter(schema, {}, node.getAttribute('id'));
    var validationObj = sa.generateValidationObj();
    if (this._customValidations[$(node).data('action')] != null) {
      this._deepExtend(validationObj, this._customValidations[$(node).data('action')]);
    }
    $(node).mogo().create('Validation', {
      validationObj: validationObj,
      callback: this._onFormValidate.bind(this, node),
      onUpdateValidation: function (formId, results) {}
    });
  }
};

APIPresenter.prototype._onFormValidate = function (node, result) {
  if ($(node).data('validate') && $(node).data('validate') == 'auto') {
    if (result.count == 0) this._performActionOnNode(node);
  } else if ($(node).data('validate') && $(node).data('validate') == 'only') {
    var action = this._nodeActionMap[node.getAttribute('id')];
    for (var key in this._actionCallbackMap[action]) {
      if (this._actionCallbackMap[action][key] != undefined) {
        this._actionCallbackMap[action][key](result);
      }
    }
  } else {
    if (result) this._performActionOnNode(node);
  }
  return false;
};

APIPresenter.prototype._createContentMap = function (node) {
  var contentNodes = $('[data-content]', node).toArray();
  this._addtoContentMap(contentNodes);
};

APIPresenter.prototype._addtoContentMap = function (contentNodes) {
  for (var i = 0; i < contentNodes.length; i++) {
    var contentNode = contentNodes[i];
    var contentPageName = $(contentNode).closest('[data-mogo-page]').data('mogo-page');
    var contentType = $(contentNode).closest('[data-mogo-page]').data('mogo-page-content-type');

    if (!contentType) {
      // in case any were missed
      console.warn('missing data-mogo-page-content-type on page component');
    }
    if (contentType === 'page') {
      // maintain backwards compat for type Page
      var contentRef = 'content.pages.' + contentPageName + '.' + $(contentNode).data('content');
    } else {
      // New system you need to specify full path (allow cross content type references)
      var contentRef = 'content.' + $(contentNode).data('content');
    }
    if (contentNode.getAttribute('id') == null) {
      this._contentIdIncrementer++;
      contentNode.setAttribute('id', 'Data_Content_' + this._contentIdIncrementer);
    }
    if (typeof this._contentMap[contentRef] === 'undefined') {
      this._contentMap[contentRef] = {};
    }
    this._contentMap[contentRef][contentNode.getAttribute('id')] = $(contentNode);
  }
};

APIPresenter.prototype._createBindingMap = function (node) {
  var bindedNodes = $('[data-bind]', node).toArray();
  this._addtoBindMap(bindedNodes);
};

APIPresenter.prototype._createActionViewBindMap = function (node) {
  var conditionalNodes = $('[data-view]', node).toArray();
  this._addtoActionViewBindMap(conditionalNodes);
};

APIPresenter.prototype._scanDOMForDataBind = function (databind) {
  var bindedNodes = $('[data-bind="' + databind + '"]').toArray();
  this._addtoBindMap(bindedNodes);
};

APIPresenter.prototype._addtoBindMap = function (bindedNodes) {
  for (var i = 0; i < bindedNodes.length; i++) {
    var bindedNode = bindedNodes[i];
    var binder = $(bindedNode).data('bind');
    if (bindedNode.getAttribute('id') == null) {
      this._bindIdIncrementer++;
      bindedNode.setAttribute('id', 'Data_Bind_' + this._bindIdIncrementer);
    }
    if (typeof this._bindMap[binder] === 'undefined') {
      this._bindMap[binder] = {};
    }
    this._bindMap[binder][bindedNode.getAttribute('id')] = $(bindedNode);

    // Add event listener for changes to update the model accordingly
    var namespacedEvent = 'change.' + binder;
    $(bindedNode).off(namespacedEvent);
    if (!this._isComputedProperty(binder)) {
      $(bindedNode).on(namespacedEvent, this._onBindedNodeUpdate.bind(this, $(bindedNode)));
    }
  }
};

APIPresenter.prototype._addtoActionViewBindMap = function (actionBindedNodes) {
  for (var i = 0; i < actionBindedNodes.length; i++) {
    var actionBindedNode = actionBindedNodes[i];
    if (
      actionBindedNode.getAttribute('id') == null ||
      actionBindedNode.getAttribute('id').length === 0
    ) {
      this._actionViewBindIdIncrementer++;
      actionBindedNode.setAttribute('id', 'Data_Action_View_' + this._actionViewBindIdIncrementer);
    }
    if (typeof this._actionViewBindMap[actionBindedNode.getAttribute('id')] === 'undefined') {
      this._actionViewBindMap[actionBindedNode.getAttribute('id')] = {};
    }
    this._actionViewBindMap[actionBindedNode.getAttribute('id')] = $(actionBindedNode);
  }
};

APIPresenter.prototype._onBindedNodeUpdate = function (node, event) {
  var value = node.val();
  if (value === '') {
    if (typeof node.mogo().val === 'function') {
      value = node.mogo().val();
    }
  }
  var bind = node.data('bind');
  if (this._viewMap[bind] && this._viewMap[bind].set) {
    value = this._viewMap[bind].set(value);
  }
  this.setModelProperty(bind, value);
  this._updateDependentNodes(this._bindMap, bind, value);
  this._updateDependentComputedVariables(bind);
};

APIPresenter.prototype._updateDependentNodes = function (map, bind, value) {
  var nodes = map[bind];
  if (typeof nodes === 'undefined') return;
  if (this._viewMap[bind] && this._viewMap[bind].get) value = this._viewMap[bind].get(value);
  for (var node in nodes) {
    if (!$.contains(document.body, nodes[node][0])) {
      this._removeNodeFromMap(map, bind, nodes[node]);
      continue;
    }
    this._renderDOMElement(nodes[node], value);
  }
};

APIPresenter.prototype._updateDependentComputedVariables = function (property) {
  var dependents = this._computedVarDependencies[property];
  if (typeof dependents === 'undefined') return;
  for (var i = 0; i < dependents.length; i++) {
    var property = dependents[i];
    var value = this._getComputedValue(property);
    this._updateDependentNodes(this._bindMap, property, value);
  }
};

APIPresenter.prototype._renderDOMElement = function (node, value) {
  var tagName = node.prop('tagName').toLowerCase();
  var allowHTML = $(node).data('escape-html') === false;
  var isBindableComponent;
  if (
    node.data('mogo-ui') == 'BooleanGroup' ||
    node.data('mogo-ui') == 'Collection' ||
    node.data('mogo-ui') == 'AggregateCollection'
  ) {
    isBindableComponent = true;
  }
  if (tagName === 'input' || tagName === 'select' || isBindableComponent) {
    if (typeof node.mogo().val === 'undefined') {
      node.attr('data-mogo-ui-value', value);
      if ($(node).val() != '' + value) {
        node.attr('value', value);
        node.trigger('renderDOMElement');
      }
    } else if (node.mogo().val() != value) {
      node.mogo().val(value);
      node.trigger('renderDOMElement');
    }
  } else if (allowHTML && node.html() != value) {
    node.html(value);
  } else if (node.html() != value) {
    node.html(this._htmlEscape(value));
  }
};

APIPresenter.prototype._isComputedProperty = function (property) {
  if (typeof this._computed[property] !== 'undefined') return true;
  return false;
};

APIPresenter.prototype._getComputedValue = function (property) {
  var variables = [];

  for (var i = 0; i < this._computed[property].variables.length; i++) {
    var variable = this._computed[property].variables[i];
    variable = this.getModelProperty(variable);
    if (typeof variable !== 'undefined') variables.push(variable);
    else variables.push('');
  }
  return this._computed[property].method.apply(null, variables);
};

APIPresenter.prototype._deepExtend = function (destination, source) {
  for (var property in source) {
    if (
      source[property] &&
      source[property].constructor &&
      source[property].constructor === Object
    ) {
      destination[property] = destination[property] || {};
      arguments.callee(destination[property], source[property]);
    } else {
      destination[property] = source[property];
    }
  }
  return destination;
};

APIPresenter.prototype._removeNodeFromMap = function (map, property, node) {
  var nodeId = $(node).attr('id');
  var bindedNode = map[property][nodeId];
  if (node === bindedNode) {
    delete map[property][nodeId];
  }
};

APIPresenter.prototype._generateId = function () {
  return Math.floor(Math.random() * 100) + 1;
};

APIPresenter.prototype._processResponse = function (node, data) {
  if (node instanceof HTMLFormElement && $(node).mogo().declaredClass == 'SchemaValidation') {
    $(node).mogo().validateResponse(node, data);
  }
  this._resetFormState(node);
};

APIPresenter.prototype._resetFormState = function (node) {
  var button = $(node).find('button[type="submit"]');
  if (button) {
    $(button).each(function () {
      if (!$(this).mogo().create) {
        $(this).mogo().removeDisabledLoadState();
      }
    });
  }
};

APIPresenter.prototype._htmlEscape = function (str) {
  return (str + '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

APIPresenter.prototype._htmlUnescape = function (str) {
  return (str + '')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
};

/*
    Update an HTML node's visibility by an element id.

    Arguments:
        actionNodeId [string] (required):
            the id of target HTML node.
*/
APIPresenter.prototype._updateDataViewNodeById = function (actionNodeId) {
  var ep = new ExpressionParser();
  var actionNode = this._actionViewBindMap[actionNodeId];
  var actionOrProperty = actionNode.attr('data-view');
  var postfixExpression = ep.convertToPostfix(actionOrProperty);

  for (var i = 0; i < postfixExpression.length; i++) {
    var current = postfixExpression[i];

    if (
      !ep.isSupportedOperator(current) && // check for operators and expressions
      current[0] !== "'" && // check for string
      current !== 'null' && // check for a null
      isNaN(current)
    ) {
      if (this.hasAction(current)) {
        // is this an action
        postfixExpression[i] = true;
      } else {
        var possibleValue = this.getModelProperty(current); // is this a model property
        if (typeof possibleValue === 'undefined' && this._isComputedProperty(current)) {
          // is this a computed property
          possibleValue = this._getComputedValue(current);
        }
        if (typeof possibleValue !== 'undefined') {
          postfixExpression[i] = [!!possibleValue, possibleValue];
        } else {
          postfixExpression[i] = false;
        }
      }
    } else if (current[0] === "'") {
      postfixExpression[i] = current.split("'")[1];
    } else if (current === 'null') {
      // null is treated as a keyword
      postfixExpression[i] = null;
    }
  }

  var result = ep.evaluate(postfixExpression);
  if (result) {
    if (
      actionNode.attr('data-view-url') &&
      actionNode.attr('data-view-url-state') !== 'pending' &&
      actionNode.attr('data-view-url-state') !== 'loaded'
    ) {
      actionNode.attr('data-view-url-state', 'pending');
      actionNode.load(
        actionNode.attr('data-view-url'),
        function (container, response, status, error) {
          if (status === 'error') {
            container.html('Could not load section');
            container.show();
          } else {
            // Manually parse if parent page is still part of the DOM (ie destroy() hasn't
            // been called since the page fragment started loading)
            var page = container.closest('[data-mogo-page]');
            if (page.parent().length) {
              var children = container.children();
              for (var j = 0; j < children.length; j++) {
                if ($(children[j]).mogo().parse) $(children[j]).mogo().parse();
              }

              // Scan DOM
              mogo.ready(this.scanDOM.bind(this, container));

              container.attr('data-view-url-state', 'loaded');
            }
          }
        }.bind(this, actionNode)
      );
    } else {
      actionNode.attr('data-view-state', 'shown');
    }
  } else {
    actionNode.attr('data-view-state', 'hidden');
  }
};

/*
    Update HTML nodes' value by a given key, which could be either
     a model property or a computed variable.

    Arguments:
        propertyOrComputed [string] (required):
            a model property or a computed variable.
*/
APIPresenter.prototype._updateDataBindNodeByKey = function (propertyOrComputed) {
  var value = '';
  if (propertyOrComputed.indexOf('.') >= 0) {
    // model property
    value = this.getModelProperty(propertyOrComputed);
    if (typeof value === 'undefined') value = '';
  } else {
    // a computed value in presenter
    if (this._isComputedProperty(propertyOrComputed)) {
      value = this._getComputedValue(propertyOrComputed);
    }
  }
  this._updateDependentNodes(this._bindMap, propertyOrComputed, value);
};

/*
    Update HTML nodes depending on the changed model property or a computed variable.

    Arguments:
        propertyOrComputed [string] (required):
            a model property or a computed variable.
*/
APIPresenter.prototype._updateDependentDOM = function (propertyOrComputed) {
  if (!propertyOrComputed) return;

  var updatedValues = new Set();
  updatedValues.add(propertyOrComputed);
  for (var cv in this._computed) {
    if (this._computed[cv]['variables'].length == 0) {
      updatedValues.add(cv);
    } else {
      for (var i = 0; i < this._computed[cv]['variables'].length; i++) {
        if (
          this._computed[cv]['variables'][i] == propertyOrComputed ||
          this._computed[cv]['variables'][i].indexOf(propertyOrComputed + '.') == 0
        ) {
          updatedValues.add(cv);
          break;
        }
      }
    }
  }

  // data-bind nodes
  var updatedBindMap = new Set(updatedValues);
  for (var bm in this._bindMap) {
    if (bm.indexOf(propertyOrComputed) > -1) {
      updatedBindMap.add(bm);
    }
  }
  updatedBindMap.forEach(this._updateDataBindNodeByKey.bind(this));

  // Parse data-view nodes
  for (var actionNodeId in this._actionViewBindMap) {
    var actionNode = this._actionViewBindMap[actionNodeId];
    var actionOrProperty = actionNode.attr('data-view');

    var dependentUpdated = false;
    updatedValues.forEach(function (value) {
      // TODO: need better calculation to exclude cases that
      // 'actionOrProperty' has a substring accidentally matching 'value'
      // or 'actionOrProperty' is in a complex format
      // (e.g. actionOrProperty = (model1.prop1 + model2.prop1.prop2 > model3.prop1) and
      // value = model2.prop1, this case should be included)
      // (e.g. actionOrProperty = abc.def.ghi and value = def, this case should be excluded)
      if (actionOrProperty.indexOf(value) > -1) {
        dependentUpdated = true;
      }
    });
    if (dependentUpdated) {
      this._updateDataViewNodeById(actionNodeId);
    }
  }
};
